import axios from '../utils/axiosConfig';
import { API_BASE_URL } from '../utils/config';

const AUTH_API_BASE_URL = `${API_BASE_URL}/auth`;

// ============================================================================
// Personal Access Tokens (PAT) API
// ============================================================================

/**
 * 建立個人存取 Token (PAT)
 * @param {string} name - Token 名稱
 * @param {number} expiresInDays - 過期天數 (可選, 1-365)
 * @returns {Promise<{token_id: string, name: string, token: string, ...}>}
 */
export const createPersonalToken = async (name, expiresInDays = null) => {
  try {
    const payload = { name };
    if (expiresInDays) payload.expires_in_days = expiresInDays;

    const response = await axios.post(`${AUTH_API_BASE_URL}/tokens`, payload);
    return response.data;
  } catch (error) {
    console.error('建立個人存取 Token 失敗:', error);
    throw error;
  }
};

/**
 * 列出自己的所有 Personal Access Tokens
 * @returns {Promise<{tokens: Array}>}
 */
export const listPersonalTokens = async () => {
  try {
    const response = await axios.get(`${AUTH_API_BASE_URL}/tokens`);
    return response.data;
  } catch (error) {
    console.error('獲取 Token 列表失敗:', error);
    throw error;
  }
};

/**
 * 撤銷個人存取 Token
 * @param {string} tokenId - Token ID
 * @returns {Promise<{message: string, success: boolean}>}
 */
export const revokePersonalToken = async (tokenId) => {
  try {
    const response = await axios.delete(`${AUTH_API_BASE_URL}/tokens/${tokenId}`);
    return response.data;
  } catch (error) {
    console.error('撤銷 Token 失敗:', error);
    throw error;
  }
};

// ============================================================================
// Permission API
// ============================================================================

/**
 * 查看自己的所有權限
 * @returns {Promise<{principal_type: string, principal_id: string, permissions: Array}>}
 */
export const getMyPermissions = async () => {
  try {
    const response = await axios.get(`${AUTH_API_BASE_URL}/permissions/me`);
    return response.data;
  } catch (error) {
    console.error('獲取我的權限失敗:', error);
    throw error;
  }
};

/**
 * 取得當前用戶在特定資源上的角色和權限
 * 用於前端根據權限控制 UI 元素
 * @param {string} resourceType - 資源類型 (dp_project, dp_schema, knowledge_base)
 * @param {string} resourceId - 資源 ID
 * @returns {Promise<{resource_type: string, resource_id: string, role_id: string|null, is_owner: boolean, permissions: Object}>}
 */
export const getMyRoleOnResource = async (resourceType, resourceId) => {
  try {
    const response = await axios.get(
      `${AUTH_API_BASE_URL}/permissions/${resourceType}/${resourceId}/my-role`
    );
    return response.data;
  } catch (error) {
    console.error('獲取我的角色權限失敗:', error);
    throw error;
  }
};

/**
 * 批次取得當前用戶在多個資源上的權限
 * 用於列表頁面根據權限控制 UI 元素（如編輯、刪除按鈕）
 * @param {string} resourceType - 資源類型 (dp_project, dp_schema, knowledge_base)
 * @param {string[]} resourceIds - 資源 ID 列表 (最多 100 個)
 * @returns {Promise<{resourceType: string, permissions: Object}>}
 * @example
 * const { permissions } = await batchGetMyPermissions('dp_schema', ['schema1', 'schema2']);
 * // permissions = {
 * //   'schema1': { canRead: true, canWrite: true, canDelete: false, ... },
 * //   'schema2': { canRead: true, canWrite: false, canDelete: false, ... }
 * // }
 */
export const batchGetMyPermissions = async (resourceType, resourceIds) => {
  try {
    if (!resourceIds || resourceIds.length === 0) {
      return { resourceType, permissions: {} };
    }

    const response = await axios.post(`${AUTH_API_BASE_URL}/permissions/batch`, {
      resourceType,
      resourceIds,
    });
    return response.data;
  } catch (error) {
    console.error('批次獲取權限失敗:', error);
    throw error;
  }
};

/**
 * 查看資源的權限設定
 * @param {string} resourceType - 資源類型 (dp_project, dp_schema, knowledge_base)
 * @param {string} resourceId - 資源 ID
 * @returns {Promise<{resource_type: string, resource_id: string, assignments: Array}>}
 */
export const getResourcePermissions = async (resourceType, resourceId) => {
  try {
    const response = await axios.get(
      `${AUTH_API_BASE_URL}/permissions/${resourceType}/${resourceId}`
    );
    return response.data;
  } catch (error) {
    console.error('獲取資源權限失敗:', error);
    throw error;
  }
};

/**
 * 授予權限
 * @param {string} resourceType - 資源類型
 * @param {string} resourceId - 資源 ID
 * @param {string} principalType - 主體類型 (user, service)
 * @param {string} principalId - 主體 ID (email 或 service account id)
 * @param {string} roleId - 角色 ID (owner, admin, editor, viewer)
 * @param {number} expiresInDays - 過期天數 (可選)
 * @returns {Promise<{message: string, success: boolean}>}
 */
export const grantPermission = async (
  resourceType,
  resourceId,
  principalType,
  principalId,
  roleId,
  expiresInDays = null
) => {
  try {
    const payload = {
      principal_type: principalType,
      principal_id: principalId,
      role_id: roleId,
    };
    if (expiresInDays) payload.expires_in_days = expiresInDays;

    const response = await axios.post(
      `${AUTH_API_BASE_URL}/permissions/${resourceType}/${resourceId}/grant`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('授予權限失敗:', error);
    throw error;
  }
};

/**
 * 撤銷權限
 * @param {string} resourceType - 資源類型
 * @param {string} resourceId - 資源 ID
 * @param {string} principalType - 主體類型
 * @param {string} principalId - 主體 ID
 * @returns {Promise<{message: string, success: boolean}>}
 */
export const revokePermission = async (resourceType, resourceId, principalType, principalId) => {
  try {
    const response = await axios.delete(
      `${AUTH_API_BASE_URL}/permissions/${resourceType}/${resourceId}/revoke`,
      {
        data: {
          principal_type: principalType,
          principal_id: principalId,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('撤銷權限失敗:', error);
    throw error;
  }
};

// ============================================================================
// Role Constants
// ============================================================================

export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
};

export const ROLE_LABELS = {
  owner: '擁有者',
  admin: '管理員',
  editor: '編輯者',
  viewer: '檢視者',
};

export const PRINCIPAL_TYPES = {
  USER: 'user',
  SERVICE_ACCOUNT: 'service',  // Backend expects 'service' not 'service_account'
};

// ============================================================================
// Service Account Available API
// ============================================================================

/**
 * 取得可用於特定資源類型的 Service Accounts 列表
 * 只回傳 scope 符合該資源類型的 Service Accounts，以及允許的角色
 * @param {string} resourceType - 資源類型 (dp_project, dp_schema, knowledge_base)
 * @returns {Promise<{service_accounts: Array<{account_id: string, name: string, allowed_roles: string[]}>}>}
 */
export const listAvailableServiceAccounts = async (resourceType) => {
  try {
    const response = await axios.get(
      `${AUTH_API_BASE_URL}/service-accounts/available`,
      { params: { resourceType } }
    );
    return response.data;
  } catch (error) {
    console.error('獲取可用 Service Accounts 失敗:', error);
    throw error;
  }
};

// ============================================================================
// Page Access API
// ============================================================================

/**
 * 取得當前使用者可存取的頁面列表
 * @returns {Promise<{user_tier: string, accessible_pages: string[]}>}
 */
export const getPageAccess = async () => {
  try {
    const response = await axios.get(`${AUTH_API_BASE_URL}/page-access`);
    return response.data;
  } catch (error) {
    console.error('獲取頁面存取權限失敗:', error);
    throw error;
  }
};
